import { captureMessage, captureException } from "@sentry/react";
import { parse } from "date-fns";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  submitExistingPatientStudyApplicationToApi,
  submitNewPatientStudyApplicationToApi,
} from "../components/contact-form/api/submitToAPI";
import {
  ApiResponse,
  FormSubmissionError,
} from "../components/contact-form/api/types";
import {
  ContactFormSchema,
  isNewPatientFormSchema,
} from "../components/contact-form/contactFormSchema";
import {
  QuestionnaireAnswers,
  useQuestionnaireAnswersState,
} from "../components/contact-form/utils/questionnaireStorage";
import { useOneClickLoginCode } from "../context/OneClickLoginCodeContext.tsx";
import { useNavigateToRedirectPage } from "../hooks/useNavigateToRedirectPage.ts";
import { useQuestionnaireQueryStates } from "../hooks/useQueryParams.ts";
import { LandingPageConfig, TrialIdConfig } from "../types/landingPage.ts";
import { calculateAge } from "../utils/dateUtils.ts";
import { identify } from "../utils/rudderstack";
import { useAnalytics } from "./useAnalytics";
import { useLandingPageConfig } from "./useLandingPageConfig.ts";
import useLandingPageIds from "./useLandingPageIds";

export interface UseContactFormSubmissionProps {
  questionnaireAnswers: QuestionnaireAnswers;
  landingPageId: string;
  isDeferredEmrConsent?: boolean;
}

export interface UseContactFormSubmissionReturn {
  isSubmitting: boolean;
  isSubmitted: boolean;
  submitForm: (formData: ContactFormSchema) => Promise<void>;
  submissionError: FormSubmissionError | null;
}

/**
 * Check if a trialIdToAutoApply value is a TrialIdConfig object with ageCohorts
 */
function isTrialIdConfigWithCohorts(
  value: string | TrialIdConfig | undefined,
): value is TrialIdConfig {
  return (
    typeof value === "object" &&
    value !== null &&
    "ageCohorts" in value &&
    Array.isArray(value.ageCohorts)
  );
}

/**
 * Checks if a user's age is eligible based on defined age cohorts
 * Returns true if:
 * - No age cohorts are defined
 * - Age matches at least one defined cohort
 * - No matching cohort but defaultTrialId exists (fallback)
 * - ConditionalRouting is used (eligibility determined at runtime)
 * Returns false if:
 * - Age cohorts are defined, user doesn't match any, and no defaultTrialId
 */
function checkAgeEligibility(age: number, config: LandingPageConfig): boolean {
  // If trialIdToAutoApply is not defined or is a simple string, no age restrictions
  if (
    !config.trialIdToAutoApply ||
    typeof config.trialIdToAutoApply === "string"
  ) {
    return true;
  }

  // ConditionalRouting configs are always eligible (eligibility checked at runtime)
  if ("conditionalRouting" in config.trialIdToAutoApply) {
    return true;
  }

  if (isTrialIdConfigWithCohorts(config.trialIdToAutoApply)) {
    const ageCohorts = config.trialIdToAutoApply.ageCohorts || [];

    // No cohorts defined, assume eligible
    if (ageCohorts.length === 0) {
      return true;
    }

    // Check if age matches any cohort
    const matchingCohort = ageCohorts.find(
      (cohort: { minAgeInclusive: number; maxAgeExclusive: number }) =>
        age >= cohort.minAgeInclusive && age < cohort.maxAgeExclusive,
    );

    // If no matching cohort found:
    if (!matchingCohort) {
      // Check if defaultTrialId exists - if it does, user is still eligible
      // If not, user is ineligible due to there not being a fallback trial ID
      return "defaultTrialId" in config.trialIdToAutoApply && config.trialIdToAutoApply.defaultTrialId !== undefined;
    }

    // Matching cohort found, user is eligible
    return true;
  }

  // Fallback case - if we're here, there are no age restrictions
  return true;
}

/**
 * Hook responsible for handling contact form API mutations
 */
export function useContactFormSubmission({
  questionnaireAnswers,
  landingPageId,
  isDeferredEmrConsent,
}: UseContactFormSubmissionProps): UseContactFormSubmissionReturn {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionError, setSubmissionError] =
    useState<FormSubmissionError | null>(null);
  const [, setQuestionnaireState] = useQuestionnaireQueryStates();

  const { oneClickLoginCode, clearOneClickLoginCode } = useOneClickLoginCode();
  const { variantId, masterId } = useLandingPageIds();
  const [questionnaireResults] = useQuestionnaireAnswersState();
  const pageConfig = useLandingPageConfig();

  const navigateToRedirectPage = useNavigateToRedirectPage();

  const navigate = useNavigate();
  const { track } = useAnalytics();

  const handleContactSubmit = ({
    formData,
    response,
    isAgeEligible,
  }: {
    formData: ContactFormSchema;
    response: ApiResponse | null;
    isAgeEligible: boolean;
  }) => {
    const questionnaireQueryState = {
      ...(response ? { apiResponse: response } : { apiResponse: null }),
      formData,
      isAgeEligible,
      priorityStudyId: formData.trialSiteTrialId ?? "",
    };

    setQuestionnaireState(questionnaireQueryState);
    if (!response) {
      captureMessage(
        "Contact form submission returned an unexpected response payload",
        {
          level: "error",
          extra: { response, formData, isAgeEligible },
        },
      );
      return;
    }

    if (response.status === "needs_login") {
      navigate(`/${landingPageId}/verify`);
      return;
    }

    if (response.status === "success") {
      if (
        isDeferredEmrConsent &&
        pageConfig?.requireEmrConsent &&
        response.outcome.applicationSiteId
      ) {
        navigate(`/${landingPageId}/emr-consent`);
        return;
      }
      navigateToRedirectPage(
        questionnaireQueryState,
        questionnaireResults,
        landingPageId,
      );
      return;
    }

    captureMessage(
      "Contact form submission returned an unexpected status",
      {
        level: "error",
        extra: { response },
      },
    );
  };

  const submitForm = async (formData: ContactFormSchema) => {
    if (!pageConfig) {
      throw new Error("Page config not found");
    }

    setSubmissionError(null);
    setIsSubmitting(true);

    const dateOfBirthObject = parse(
      formData.dateOfBirth,
      "MM/dd/yyyy",
      new Date(),
    );
    const age = calculateAge(dateOfBirthObject);

    const isAgeEligible = checkAgeEligibility(age, pageConfig);

    if (!isAgeEligible) {
      // Analytics on the age ineligiblity ~ no functional impact
      let ineligibleReason = "no_matching_cohort";
      let hasDefaultTrialId = false;

      if (isTrialIdConfigWithCohorts(pageConfig.trialIdToAutoApply)) {
        hasDefaultTrialId =
          "defaultTrialId" in pageConfig.trialIdToAutoApply &&
          pageConfig.trialIdToAutoApply.defaultTrialId !== undefined;
        ineligibleReason = "no_matching_cohort_and_no_default";
      }

      track("lp.contact_form.age_ineligible", {
        age: age,
        landing_page_id: landingPageId,
        variant_id: variantId,
        master_id: masterId,
        has_cohorts: isTrialIdConfigWithCohorts(pageConfig.trialIdToAutoApply),
        reason: ineligibleReason,
        had_default_trial_id: hasDefaultTrialId,
      });
    }

    try {
      // The API call (the actual payload preparation will use getMatchingTrialId to select
      // the appropriate trial ID based on the matching age cohort)
      const { payload, responseData } = isNewPatientFormSchema(formData)
        ? await submitNewPatientStudyApplicationToApi({
            formData,
            // Pass entire object to preserve matchedStudyId and matchedStudyTrialId for multi-study flows
            questionnaireAnswers,
            landingPageId,
          })
        : await submitExistingPatientStudyApplicationToApi({
            formData,
            // Pass entire object to preserve matchedStudyId and matchedStudyTrialId for multi-study flows
            questionnaireAnswers,
            landingPageId,
            userCredentials: {
              oneClickLogin: {
                oneClickLoginCode: oneClickLoginCode || "",
              },
            },
          });

      if (!formData.trialSiteTrialId) {
        track("lp.application.no_trial_id_to_apply_with", {
          landing_page_id: landingPageId,
          variant_id: variantId,
          master_id: masterId,
        });
      }

      if (responseData?.outcome?.patientId) {
        identify(responseData.outcome.patientId.toString());

        track("lp.form.completed", {
          landing_page_id: landingPageId,
          variant_id: variantId,
          master_id: masterId,
          has_questionnaire_data:
            !!questionnaireAnswers.questionAnswers && Object.keys(questionnaireAnswers.questionAnswers).length > 0,
          zip_code_eligible: responseData.outcome.successfullyCreatedReferral,
          has_priority_study_id:
            responseData.outcome.successfullyCreatedReferral,
          has_trial_site_id: !!payload.trialSiteId,
          has_trial_id_to_auto_apply: !!formData.trialSiteTrialId,
        });

        if (responseData.outcome.successfullyCreatedReferral) {
          track("lp.application.successful", {
            landing_page_id: landingPageId,
            variant_id: variantId,
            master_id: masterId,
            patient_id: responseData.outcome.patientId,
          });
        }
      }

      setIsSubmitted(true);
      handleContactSubmit({
        formData,
        response: responseData,
        isAgeEligible: true,
      });
    } catch (error) {
      captureException(error);
      const typedError = error as FormSubmissionError;
      setSubmissionError(typedError);

      track("lp.contact_form.submission_error", {
        error_type: typedError.name || "unknown",
        error_message: typedError.message,
        landing_page_id: landingPageId,
      });
      if (typedError && typedError.name === "ApiError") {
        if (
          typedError.response?.outcome?.loginType &&
          typedError.response?.outcome?.loginHint
        ) {
          track("lp.application.one_click_login_code_expired", {
            landing_page_id: landingPageId,
            variant_id: variantId,
            master_id: masterId,
          });
          // Convert error response to API response format
          const apiResponse: ApiResponse = {
            status: "needs_login",
            outcome: typedError.response.outcome!,
          };
          handleContactSubmit({
            formData,
            response: apiResponse,
            isAgeEligible: true,
          });
        } else if (typedError.response?.message === "Duplicate Application") {
          track("duplicate.application", {
            landingPageId: landingPageId,
            variantId: variantId,
            masterId: masterId,
          });
          track("lp.application.duplicate_application", {
            landingPageId: landingPageId,
            variantId: variantId,
            masterId: masterId,
          });
        } else {
          // This catches the unlikely case where the one-click login code is completely invalid.
          // The form will (not very gracefully) revert to the new patient version.
          clearOneClickLoginCode();
          track("lp.application.one_click_login_code_invalid", {
            landing_page_id: landingPageId,
            variant_id: variantId,
            master_id: masterId,
          });
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    isSubmitted,
    submitForm,
    submissionError,
  };
}
